/**
 * Standardized error types and utilities for Argus IQ
 * Provides consistent error responses across the API
 */

import { z } from 'zod';

/** Standard error codes used across the API */
export const ErrorCode = {
  // Client errors (4xx)
  BAD_REQUEST: 'BAD_REQUEST',

  // Auth (401) — discriminated per reason (Phase C W1a-24).
  // UNAUTHORIZED stays as the generic 401 for backward compat; the
  // four below let callers distinguish *why* a token failed.
  UNAUTHORIZED: 'UNAUTHORIZED',
  MISSING_TOKEN: 'MISSING_TOKEN',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  INVALID_AUDIENCE: 'INVALID_AUDIENCE',
  /**
   * R6 P0-2 (security): sysadmin user has been demoted; their access
   * token is still cryptographically valid (within 30-min TTL) but the
   * Valkey blocklist marks them as revoked. The middleware returns 401
   * with this code so the UI knows to clear local state and redirect
   * to login, not silently retry.
   */
  SYSADMIN_REVOKED: 'SYSADMIN_REVOKED',
  /**
   * Foundations 2 (Users rail), B2a-T3: the bearer holds a valid TENANT
   * access token for somebody who is no longer LIVE in the organization the
   * token names — soft-deleted, deactivated, locked, suspended, membership
   * lapsed, or no membership at all. The token is cryptographically fine and
   * the person is not, which is why this is a 401 ("clear the session and log
   * in again") rather than a 403 ("you are who you say, and may not do this").
   *
   * Emitted by `plugins/auth.ts` on EVERY authenticated tenant request, gated
   * or not, because a token outlives the facts that justified minting it.
   */
  PRINCIPAL_NOT_LIVE: 'PRINCIPAL_NOT_LIVE',

  FORBIDDEN: 'FORBIDDEN',
  /**
   * Wave C followup (is_demo sysadmin gate, 2026-05-13):
   * 403 emitted by tenant routes when a privileged escape hatch
   * (e.g. ?includeDemo=true on the 4 list endpoints touched by
   * PR #518) is invoked by a non-sysadmin caller. Distinct from the
   * generic FORBIDDEN so the SPA / audit grep can spot abuse attempts
   * without false-positives from routine permission denials.
   */
  INSUFFICIENT_ROLE: 'INSUFFICIENT_ROLE',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',

  // Server errors (5xx)
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  DATABASE_ERROR: 'DATABASE_ERROR',
  CACHE_ERROR: 'CACHE_ERROR',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/** HTTP status code mapping for error codes */
export const errorCodeToStatus: Record<ErrorCode, number> = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  MISSING_TOKEN: 401,
  TOKEN_EXPIRED: 401,
  INVALID_TOKEN: 401,
  INVALID_AUDIENCE: 401,
  SYSADMIN_REVOKED: 401,
  PRINCIPAL_NOT_LIVE: 401,
  FORBIDDEN: 403,
  INSUFFICIENT_ROLE: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  VALIDATION_ERROR: 422,
  RATE_LIMITED: 429,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
  DATABASE_ERROR: 500,
  CACHE_ERROR: 500,
};

/** Schema for a single field error inside a validation failure.
 *
 * `path` is the dotted JSON path to the offending field (e.g. `name`,
 * `metadata.priority`, `items.0.sku`). Empty string means the error is
 * at the root of the validated payload.
 *
 * `message` is the human-readable Zod issue message ("Required",
 * "String must contain at least 1 character(s)", etc).
 *
 * `code` is the Zod issue code (`invalid_type`, `too_small`,
 * `invalid_string`, …). Optional because non-Zod producers may not
 * populate it.
 */
export const fieldErrorSchema = z.object({
  path: z.string(),
  message: z.string(),
  code: z.string().optional(),
});
export type FieldError = z.infer<typeof fieldErrorSchema>;

/** Validation-error details payload.
 *
 * Wrapped in an object (rather than a bare array) so future validation
 * kinds (form errors, top-level cross-field errors) can be added
 * without another breaking change. The SPA reads `details.fieldErrors`
 * to render inline field-level messages on create/edit forms.
 *
 * Bug H4 (UI sweep 2026-05-11): before this shape existed, the API
 * returned `{ code: 'VALIDATION_ERROR', message: 'Validation failed' }`
 * with no `details`. The SPA showed a generic "Validation failed" toast
 * with no hint about which field was wrong — every sysadmin catalog
 * create button hit this bug.
 */
export const validationErrorDetailsSchema = z.object({
  fieldErrors: z.array(fieldErrorSchema),
});
export type ValidationErrorDetails = z.infer<typeof validationErrorDetailsSchema>;

/** Back-compat alias for the previous single-field shape.
 *
 * Some pre-existing route handlers and tests refer to
 * `ValidationErrorDetail` directly. Keep it pointing at FieldError so
 * callers that haven't migrated keep compiling.
 */
export const validationErrorDetailSchema = fieldErrorSchema;
export type ValidationErrorDetail = FieldError;

/** Schema for standardized API error response.
 *
 * `details` is typed as `z.unknown()` because routes use it for a
 * variety of structured payloads — not just field-level validation
 * errors. Catalog-publish routes, for instance, return
 * `details: { danglingToolSlugs: string[], inactiveFunctionIds: ... }`
 * to drive the dependency-resolver UI. Forcing a single shape would
 * break those payloads.
 *
 * For Zod-validation failures specifically, `details` matches
 * {@link validationErrorDetailsSchema} (i.e. `{ fieldErrors: [...] }`).
 */
export const apiErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
    timestamp: z.string(),
  }),
});
export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>;

/** Custom application error class */
export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly statusCode: number;
  public readonly details?: unknown;
  public readonly isOperational: boolean;

  constructor(
    code: ErrorCode,
    message: string,
    options?: {
      details?: unknown;
      cause?: Error;
      isOperational?: boolean;
    }
  ) {
    super(message, { cause: options?.cause });
    this.name = 'AppError';
    this.code = code;
    this.statusCode = errorCodeToStatus[code];
    this.details = options?.details;
    this.isOperational = options?.isOperational ?? true;
    Error.captureStackTrace(this, this.constructor);
  }

  /** Convert to API error response format */
  toResponse(requestId?: string): ApiErrorResponse {
    return {
      success: false,
      error: {
        code: this.code,
        message: this.message,
        details: this.details,
        requestId,
        timestamp: new Date().toISOString(),
      },
    };
  }
}

/** Factory functions for common errors */
export const Errors = {
  badRequest: (message: string, details?: unknown) =>
    new AppError(ErrorCode.BAD_REQUEST, message, { details }),

  unauthorized: (message = 'Authentication required') =>
    new AppError(ErrorCode.UNAUTHORIZED, message),

  // Discriminated 401s — Phase C W1a-24
  missingToken: (message = 'Missing access token') =>
    new AppError(ErrorCode.MISSING_TOKEN, message),
  tokenExpired: (message = 'Access token has expired') =>
    new AppError(ErrorCode.TOKEN_EXPIRED, message),
  invalidToken: (message = 'Invalid access token') =>
    new AppError(ErrorCode.INVALID_TOKEN, message),
  invalidAudience: (message = 'Token audience does not match this surface') =>
    new AppError(ErrorCode.INVALID_AUDIENCE, message),
  /**
   * R6 P0-2 (security): emitted by `requireSysadmin` when the bearer
   * holds a valid sysadmin JWT for a user that has been demoted (the
   * Valkey blocklist `sysadmin:revoked:{userId}` is set). The client
   * should clear its sysadmin session and route to the platform login
   * page.
   */
  sysadminRevoked: (message = 'Sysadmin access has been revoked') =>
    new AppError(ErrorCode.SYSADMIN_REVOKED, message),

  /**
   * Foundations 2 (Users rail), B2a-T3 — the person behind a valid tenant
   * access token is no longer live in the organization it names. The client
   * should clear its session and return to the login page: retrying the same
   * token can only fail the same way.
   */
  principalNotLive: (message = 'This account is no longer active') =>
    new AppError(ErrorCode.PRINCIPAL_NOT_LIVE, message),

  forbidden: (message = 'Access denied') =>
    new AppError(ErrorCode.FORBIDDEN, message),

  /**
   * Wave C followup (is_demo sysadmin gate, 2026-05-13):
   * 403 emitted by tenant routes when a privileged escape hatch
   * (e.g. ?includeDemo=true on the 4 list endpoints touched by
   * PR #518) is invoked by a non-sysadmin caller.
   */
  insufficientRole: (message = 'Insufficient role for this operation') =>
    new AppError(ErrorCode.INSUFFICIENT_ROLE, message),

  notFound: (resource: string, id?: string) =>
    new AppError(
      ErrorCode.NOT_FOUND,
      id ? `${resource} with ID '${id}' not found` : `${resource} not found`
    ),

  conflict: (message: string) =>
    new AppError(ErrorCode.CONFLICT, message),

  /** Build a 422 VALIDATION_ERROR.
   *
   * Accepts either:
   *   - `FieldError[]` — legacy shape; auto-wrapped into
   *     `{ fieldErrors: [...] }` so the SPA always sees the same key.
   *   - `ValidationErrorDetails` — preferred new shape.
   *   - Any other object (e.g. catalog `danglingToolSlugs` payload).
   */
  validationError: (details: FieldError[] | ValidationErrorDetails | unknown) => {
    const wrapped = Array.isArray(details) ? { fieldErrors: details } : details;
    return new AppError(ErrorCode.VALIDATION_ERROR, 'Validation failed', {
      details: wrapped,
    });
  },

  rateLimited: (message = 'Rate limit exceeded') =>
    new AppError(ErrorCode.RATE_LIMITED, message),

  internal: (message = 'An internal error occurred', cause?: Error) =>
    new AppError(ErrorCode.INTERNAL_ERROR, message, {
      cause,
      isOperational: false,
    }),

  serviceUnavailable: (service: string) =>
    new AppError(ErrorCode.SERVICE_UNAVAILABLE, `${service} is unavailable`),

  database: (message: string, cause?: Error) =>
    new AppError(ErrorCode.DATABASE_ERROR, message, {
      cause,
      isOperational: false,
    }),

  cache: (message: string, cause?: Error) =>
    new AppError(ErrorCode.CACHE_ERROR, message, {
      cause,
      isOperational: false,
    }),
};

/** Flatten a ZodError into an array of {@link FieldError}.
 *
 * Each issue becomes one entry. `path` is dotted (`metadata.priority`),
 * matching what SPA forms use to wire inline errors to field names.
 *
 * Used by:
 *   - the Fastify error-handler plugin for response-serialization
 *     failures (`fastify-type-provider-zod` throws a
 *     `ResponseSerializationError` with `cause: ZodError`).
 *   - any route that calls `schema.parse()` manually and wants to
 *     re-throw via `Errors.validationError(zodErrorToFieldErrors(e))`.
 */
export function zodErrorToFieldErrors(error: z.core.$ZodError): FieldError[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    message: issue.message,
    code: issue.code,
  }));
}

/** Build a ValidationErrorDetails payload from a ZodError. */
export function zodErrorToValidationDetails(
  error: z.core.$ZodError,
): ValidationErrorDetails {
  return { fieldErrors: zodErrorToFieldErrors(error) };
}

/** Back-compat: previous name returned a bare array. Now returns the
 * wrapped `{ fieldErrors: [...] }` object so callers that forwarded
 * the return value into `error.details` produce the new SPA-friendly
 * shape automatically. Existing code that destructured the array
 * needs to switch to {@link zodErrorToFieldErrors}.
 */
export function zodErrorToDetails(
  error: z.core.$ZodError,
): ValidationErrorDetails {
  return zodErrorToValidationDetails(error);
}

/** Shape of a single entry in `FastifyError.validation` when the Fastify
 * validator is `fastify-type-provider-zod`. The plugin maps each Zod
 * issue to this object:
 *   {
 *     keyword: issue.code,
 *     instancePath: '/' + issue.path.join('/'),
 *     schemaPath:   '#/' + issue.path.join('/') + '/' + issue.code,
 *     message:      issue.message,
 *     params:       { ...rest of the issue }
 *   }
 * See node_modules/fastify-type-provider-zod/dist/cjs/errors.cjs.
 */
interface FastifySchemaIssueLike {
  keyword?: string;
  instancePath?: string;
  message?: string;
}

/** Convert a `FastifyError.validation` array (as produced by
 * fastify-type-provider-zod's `validatorCompiler`) into our
 * `FieldError[]` shape. Drops the leading "/" from `instancePath` and
 * converts internal "/" to "." so SPA form-field paths line up with
 * Zod's natural dotted path notation.
 */
export function fastifyValidationToFieldErrors(
  validation: ReadonlyArray<FastifySchemaIssueLike>,
): FieldError[] {
  return validation.map((issue) => {
    const raw = issue.instancePath ?? '';
    // "/" → "", "/name" → "name", "/items/0/sku" → "items.0.sku"
    const path = raw.startsWith('/')
      ? raw.slice(1).replace(/\//g, '.')
      : raw.replace(/\//g, '.');
    return {
      path,
      message: issue.message ?? 'Invalid value',
      code: issue.keyword,
    };
  });
}

/** Type guard for AppError */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}
